import React, { useState, useEffect, useRef, useMemo } from "react";
import UserSection from "../../Services/User/UserLogin";
import UserTopbar from "../UserTopbar";
import { useOutletContext } from "react-router-dom";
import {
  Business,
  Assignment,
  ContactMail,
  TrendingUp,
  Download,
  GraphicEq,
  People,
  PersonOutline,
  CalendarMonthOutlined,
  RestartAlt,
  KeyboardArrowDown,
  ArrowForward,
} from "@mui/icons-material";
import axios from "axios";
import Apiurl from "../../Helpers/apiUrl";
import Swal from "sweetalert2";
import { IconButton, CircularProgress } from "@mui/material";
import Loader from "../utils/Loader";
import withDashboardAccess from "../../hoc/withDashboardAccess";

// ── Design Tokens ──────────────────────────────────────────────────────────
const INK = "#0F172A";
const PAPER = "#F8FAFC";
const LINE = "#E2E8F0";
const GOLD = "#D97706";
const TEAL = "#0D9488";
const CORAL = "#E11D48";
const SLATE = "#64748B";

const fmt = (n) => (n === undefined || n === null ? "0" : Number(n).toLocaleString("en-IN"));

// ── Shared Subcomponents ───────────────────────────────────────────────────

function Dropdown({ label, icon, value, options, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 h-9 px-3 text-[12.5px] font-medium rounded-lg border transition-all shadow-sm ${disabled
          ? "opacity-50 cursor-not-allowed bg-slate-100 border-slate-200 text-slate-400"
          : selected
            ? "bg-slate-900 border-slate-900 text-white font-semibold"
            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
          }`}
      >
        <span className={selected ? "text-slate-300" : "text-slate-400"}>{icon}</span>
        <span>{selected ? selected.label : label}</span>
        <KeyboardArrowDown
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          sx={{ fontSize: 16, opacity: selected ? 0.8 : 0.5 }}
        />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1.5 w-56 bg-white rounded-xl z-50 py-1.5 shadow-xl border overflow-hidden max-h-60 overflow-y-auto"
          style={{ borderColor: LINE }}
        >
          <div
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="px-3 py-1.5 text-[12px] font-semibold text-slate-400 hover:bg-slate-50 hover:text-slate-700 cursor-pointer transition-colors"
          >
            All Options
          </div>
          {options.map((o) => (
            <div
              key={o.value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`px-3 py-2 text-[12.5px] cursor-pointer transition-colors hover:bg-slate-50 ${String(o.value) === String(value) ? "font-semibold text-slate-900 bg-slate-100/70" : "text-slate-600"
                }`}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DateRangePicker({ fromDate, toDate, setFromDate, setToDate }) {
  return (
    <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5 shadow-sm hover:border-slate-300 transition-colors h-9">
      <div className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-50/80 rounded-md h-full">
        <CalendarMonthOutlined sx={{ fontSize: 13 }} className="text-slate-400" />
        <span className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">From</span>
        <input
          type="date"
          value={fromDate || ""}
          onChange={(e) => setFromDate(e.target.value)}
          className="bg-transparent text-slate-700 border-none font-medium focus:outline-none text-[11px] cursor-pointer"
        />
      </div>

      <ArrowForward sx={{ fontSize: 10 }} className="text-slate-300 shrink-0" />

      <div className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-50/80 rounded-md h-full">
        <span className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">To</span>
        <input
          type="date"
          value={toDate || ""}
          onChange={(e) => setToDate(e.target.value)}
          className="bg-transparent text-slate-700 border-none font-medium focus:outline-none text-[11px] cursor-pointer"
        />
      </div>
    </div>
  );
}

function YearSelector({ years, selectedYear, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Find selected item to retrieve its label
  const selectedItem = years.find((y) => String(y.value) === String(selectedYear));

  return (
    <div
      ref={ref}
      className={`relative inline-block transition-opacity ${disabled ? "opacity-40 pointer-events-none" : "opacity-100"}`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 h-9 px-3 bg-white border border-slate-200 rounded-lg shadow-sm hover:border-slate-300 transition-all select-none text-[12px]"
      >
        <CalendarMonthOutlined sx={{ fontSize: 15 }} className="text-slate-400" />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          Year:
        </span>
        <span className="font-semibold text-slate-800">
          {selectedItem ? selectedItem.label : "Select"}
        </span>
        <KeyboardArrowDown
          className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          sx={{ fontSize: 16 }}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-32 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50 max-h-56 overflow-y-auto">
          {years.map((y) => {
            const isSelected = String(y.value) === String(selectedYear);
            return (
              <div
                key={y.value}
                onClick={() => {
                  onChange(y.value); // Sends 2026 to state/API
                  setOpen(false);
                }}
                className={`px-3 py-1.5 text-[12px] font-medium cursor-pointer transition-colors flex items-center justify-between ${isSelected
                  ? "bg-slate-900 text-white font-semibold"
                  : "text-slate-700 hover:bg-slate-50"
                  }`}
              >
                <span>{y.label}</span> {/* Displays 2026-2027 */}
                {isSelected && <span className="text-[10px]">✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function Stat({ label, value, accent = INK }) {
  return (
    <div className="flex flex-col justify-center px-2 sm:px-4 py-3 min-w-0">
      <div
        className="text-base sm:text-lg md:text-xl leading-tight font-bold tabular-nums break-words"
        style={{ fontFamily: "'JetBrains Mono', monospace", color: accent }}
      >
        {fmt(value)}
      </div>

      <div className="text-[10px] sm:text-[11px] font-medium text-slate-500 mt-1 whitespace-normal break-words">
        {label}
      </div>
    </div>
  );
}




function StatGroup({ eyebrow, children }) {
  return (
    <div className="flex flex-col flex-1 min-w-0">
      <div className="px-4 pt-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {eyebrow}
      </div>

      <div className="grid grid-cols-2 gap-0">
        {children}
      </div>
    </div>
  );
}



function Panel({ title, icon, accent = GOLD, actions, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden flex flex-col" style={{ borderColor: LINE }}>
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: LINE }}>
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-slate-50" style={{ color: accent }}>
            {icon}
          </div>
          <h3 className="text-[13.5px] font-semibold text-slate-900 tracking-tight">{title}</h3>
        </div>
        {actions && <div className="flex items-center gap-1.5">{actions}</div>}
      </div>
      <div className="flex-1 p-2">{children}</div>
    </div>
  );
}

function DataTable({ columns, rows, empty, renderRow }) {
  return (
    <div className="overflow-y-auto" style={{ maxHeight: "320px" }}>
      <table className="w-full text-left">
        <thead>
          <tr className="border-b" style={{ borderColor: LINE }}>
            {columns.map((c, i) => (
              <th
                key={i}
                className={`sticky top-0 bg-white/95 backdrop-blur-sm px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 ${c.align === "right" ? "text-right" : "text-left"
                  }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows && rows.length > 0 ? (
            rows.map((row, i) => renderRow(row, i))
          ) : (
            <tr>
              <td colSpan={columns.length} className="px-3 py-8 text-center text-[12px] text-slate-400 font-medium">
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Standalone Custom Line Graph Component ─────────────────────────────────

function MonthlyLineGraph({ monthlyData }) {

  const months = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

  // Flexible value extractor matching multiple potential API object structures
  const values = months.map((_, i) => {
    if (!monthlyData || !Array.isArray(monthlyData)) return 0;

    const targetMonthNum = i + 1;
    const match = monthlyData.find((m) => {
      if (!m) return false;
      const mNum = Number(m.month || m.month_num || m.month_number || m.id);
      if (mNum === targetMonthNum) return true;

      const mName = String(m.month_name || m.name || m.month || "").toLowerCase();
      return mName.startsWith(months[i].toLowerCase());
    });

    if (!match) return 0;

    // Read numerical value from possible backend keys
    const rawVal = match.count ?? match.total ?? match.quotations ?? match.project_count ?? match.total_quotations ?? match.val ?? 0;
    return Number(rawVal) || 0;
  });

  const peakValue = Math.max(...values);
  const maxVal = peakValue > 0 ? Math.ceil(peakValue * 1.15) : 10; // Dynamic scale height
  const width = 1000;
  const height = 280;
  const padding = 45;

  const points = values.map((val, idx) => {
    const x = padding + (idx / (months.length - 1)) * (width - padding * 2);
    const y = height - padding - (val / maxVal) * (height - padding * 2);
    return { x, y, val, month: months[idx] };
  });

  const pathD = points.reduce((acc, p, i) => (i === 0 ? `M ${p.x},${p.y}` : `${acc} L ${p.x},${p.y}`), "");
  const areaD = `${pathD} L ${points[points.length - 1].x},${height - padding} L ${points[0].x},${height - padding} Z`;

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto min-w-[750px]">
        {/* Horizontal Background Grid Lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
          const y = height - padding - ratio * (height - padding * 2);
          const gridVal = Math.round(ratio * maxVal);
          return (
            <g key={i}>
              <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#E2E8F0" strokeDasharray="4 4" />
              <text x={padding - 12} y={y + 4} textAnchor="end" className="text-[11px] fill-slate-400 font-mono font-medium">
                {gridVal}
              </text>
            </g>
          );
        })}

        {/* Gradient fill under line */}
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0D9488" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#0D9488" stopOpacity="0.0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#lineGrad)" />

        {/* Smooth teal line */}
        <path d={pathD} fill="none" stroke="#0D9488" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

        {/* Plot Data Points & Persistent Value Labels */}
        {points.map((p, i) => (
          <g key={i} className="group cursor-pointer">
            {/* Value label permanently displayed above each non-zero point */}
            <text
              x={p.x}
              y={p.y - 12}
              textAnchor="middle"
              className={`text-[12px] font-bold font-mono transition-colors ${p.val > 0 ? "fill-teal-700" : "fill-slate-300"
                }`}
            >
              {fmt(p.val)}
            </text>

            {/* Point Node */}
            <circle cx={p.x} cy={p.y} r={p.val > 0 ? "5" : "3"} fill={p.val > 0 ? "#0D9488" : "#94A3B8"} className="transition-all duration-200 group-hover:r-7" />
            <circle cx={p.x} cy={p.y} r="9" fill="#0D9488" fillOpacity="0.2" className="opacity-0 group-hover:opacity-100 transition-opacity" />

            {/* Month Label */}
            <text x={p.x} y={height - 14} textAnchor="middle" className="text-[11.5px] font-semibold fill-slate-600">
              {p.month}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

const rowCls = "hover:bg-slate-50/80 transition-colors duration-150";
const cellCls = "px-3 py-2.5 text-[12.5px]";

// ── Main UserDashboard Component ───────────────────────────────────────────

const UserDashboard = () => {
  const currentYears = new Date().getFullYear();
  const { isSidebarOpen } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashbaord] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [user, setUser] = useState([]);
  const [latestUserData, setLatestUserData] = useState([]);
  const [g3Peoduct, setg3Peoduct] = useState([]);
  const [segmentSkuData, setsegmenData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYears);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const fetchUserDashboard = async () => {
    try {
      setLoading(true);
      const response = await UserSection.userDashboard({
        user: selectedUserId,
        year: fromDate || toDate ? null : selectedYear,
        from: fromDate,
        to: toDate,
      });

      setDashbaord(response.data);
      setUser(response.data.BspDspUser || []);
      setLatestUserData(response.data.latestUser || []);
      setg3Peoduct(response.data.g3productsegment || []);
      setsegmenData(response.data.priceSegment || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedUserId, fromDate, toDate]);

  const downloadFile = async (excelType = "sku") => {
    try {
      setLoading(true);
      const token = localStorage.getItem("u_token");
      const response = await axios.get(
        `${Apiurl}dashboardExcel?type=${excelType}&year=${fromDate || toDate ? null : selectedYear
        }&user_id=${selectedUserId}&from=${fromDate}&to=${toDate}`,
        {
          headers: {
            Authorization: "Bearer " + token,
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          responseType: "blob",
        }
      );

      const contentDisposition = response.headers["content-disposition"];
      let filename = "export.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Swal.fire({
        icon: "warning",
        title: "Download Failed",
        text: error.message || "Something went wrong while downloading.",
        timer: 3000,
      });
    }
  };

  const currentYear = new Date().getFullYear();
  const startYear = 2022;
  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => {
      const year = currentYear - i;
      return {
        value: year,
        label: `${year}-${year + 1}`
      };
    }
  );

  const userOptions = useMemo(
    () =>
      (user || [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((u) => ({ value: u.id, label: u.name })),
    [user]
  );

  const yearOptions = useMemo(() => years, [years]);

  const handleClearFilters = () => {
    setSelectedUserId("");
    setFromDate(null);
    setToDate(null);
    setSelectedYear(currentYears);
  };

  const hasDateRange = Boolean(fromDate || toDate);

  return (
    <>
      {loading && <Loader />}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap');`}</style>

      <UserTopbar heading="Dashboard Overview" sidebar={isSidebarOpen} />

      <div className="min-h-screen antialiased pb-16" style={{ background: PAPER, fontFamily: "Inter, system-ui, sans-serif" }}>
        {/* Modernized Filter Header Bar */}
        <div
          className={`fixed top-16 right-0 z-30 transition-all duration-300 ${isSidebarOpen ? "left-52" : "left-20"}`}
          style={{ background: "rgba(248, 250, 252, 0.9)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${LINE}` }}
        >
          <div className="max-w-[1600px] mx-auto px-6 py-3 flex flex-wrap items-center gap-3">
            <Dropdown
              label="All Users"
              icon={<PersonOutline sx={{ fontSize: 16 }} />}
              value={selectedUserId}
              options={userOptions}
              onChange={setSelectedUserId}
            />

            <span className="h-5 w-px bg-slate-200 my-auto" />

            <DateRangePicker fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} />

            <span className="h-5 w-px bg-slate-200 my-auto" />

            <YearSelector
              years={years}
              selectedYear={hasDateRange ? "" : selectedYear}
              onChange={(v) => setSelectedYear(v || currentYears)}
              disabled={hasDateRange}
            />

            <div className="ml-auto flex items-center gap-3">
              {loading && <CircularProgress size={16} thickness={5} sx={{ color: INK }} />}
              <button
                type="button"
                onClick={handleClearFilters}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-slate-500 hover:text-rose-600 bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-200 rounded-lg shadow-sm transition-all"
              >
                <RestartAlt sx={{ fontSize: 16 }} /> Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Spacer for sticky header */}
        <div className="h-16" />

        <main className="max-w-[1600px] mx-auto px-6 pt-6 flex flex-col gap-6">
          {/* Scoreboard Overview */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: LINE }}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 divide-y xl:divide-y-0 xl:divide-x divide-slate-100">
              <StatGroup eyebrow="Workspace">
                <Stat label="Clients" value={dashboard?.clients} accent={INK} />
                <Stat label="Quotations" value={dashboard?.totalProject} accent={GOLD} />
                <Stat label="Drafts" value={dashboard?.toaldraft} accent={SLATE} />
              </StatGroup>

              <StatGroup eyebrow="Pipeline">
                <Stat label="Enquiries" value={dashboard?.Enquiries} accent={INK} />
                <Stat label="In Pipeline" value={dashboard?.Pipeline} accent={GOLD} />
                <Stat label="Converted" value={dashboard?.Conversion} accent={TEAL} />
                <Stat label="Lost" value={dashboard?.Lost} accent={CORAL} />
              </StatGroup>

              <StatGroup eyebrow="Quotation Volume">
                <Stat label="Today" value={dashboard?.todayQuotation} accent={INK} />
                <Stat label="This Week" value={dashboard?.weekOfQuotation} accent={SLATE} />
                <Stat label="This Month" value={dashboard?.monthQuotation} accent={GOLD} />
                <Stat label="This Year" value={dashboard?.yearQuotaion} accent={TEAL} />
              </StatGroup>

              <StatGroup eyebrow="Quotations by Role">
                <Stat label="Sales Enabler" value={dashboard?.saleEnablersQuotation} accent={INK} />
                <Stat label="IC" value={dashboard?.lcquotation} accent={SLATE} />
                <Stat label="BSP" value={dashboard?.bspquotation} accent={GOLD} />
                <Stat label="DSP" value={dashboard?.dspquotation} accent={TEAL} />
              </StatGroup>
            </div>
          </div>

          {/* Full-Width Monthly Performance Line Graph */}
          <Panel title="Monthly Performance Trends" icon={<GraphicEq sx={{ fontSize: 18 }} />} accent={TEAL}>
            <div className="p-4 w-full">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <CircularProgress size={28} thickness={4} sx={{ color: INK }} />
                </div>
              ) : (
                <MonthlyLineGraph monthlyData={dashboard?.monthly_quotation} />
              )}
            </div>
          </Panel>

          {/* Tables Row 1 */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Panel
              title="Top Clients"
              icon={<Business sx={{ fontSize: 18 }} />}
              accent={GOLD}
              actions={
                <>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                    {dashboard.latestClient?.length || 0}
                  </span>
                  <IconButton size="small" onClick={() => downloadFile("client")}>
                    <Download sx={{ fontSize: 18 }} className="text-emerald-600" />
                  </IconButton>
                </>
              }
            >
              <DataTable
                columns={[{ label: "#" }, { label: "Client Name" }, { label: "Projects", align: "right" }]}
                rows={dashboard.latestClient}
                empty="No client records available"
                renderRow={(client, index) => (
                  <tr key={index} className={rowCls}>
                    <td className={`${cellCls} text-slate-400 font-medium`}>{index + 1}</td>
                    <td className={`${cellCls} font-semibold text-slate-800`}>{client.clientname}</td>
                    <td
                      className={`${cellCls} text-right tabular-nums font-semibold text-slate-700`}
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {fmt(client.project_count)}
                    </td>
                  </tr>
                )}
              />
            </Panel>

            <Panel
              title="Top SKUs"
              icon={<Assignment sx={{ fontSize: 18 }} />}
              accent={TEAL}
              actions={
                <>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">
                    {dashboard.latestSku?.length || 0}
                  </span>
                  <IconButton size="small" onClick={() => downloadFile("sku")}>
                    <Download sx={{ fontSize: 18 }} className="text-emerald-600" />
                  </IconButton>
                </>
              }
            >
              <DataTable
                columns={[{ label: "#" }, { label: "SKU Code" }, { label: "Usage Count", align: "right" }]}
                rows={dashboard?.latestSku}
                empty="No SKU records available"
                renderRow={(sku, index) => (
                  <tr key={index} className={rowCls}>
                    <td className={`${cellCls} text-slate-400 font-medium`}>{index + 1}</td>
                    <td className={`${cellCls} font-semibold text-slate-800`}>{sku.sku_code}</td>
                    <td
                      className={`${cellCls} text-right tabular-nums font-semibold text-slate-700`}
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {fmt(sku.usage_count)}
                    </td>
                  </tr>
                )}
              />
            </Panel>

            <Panel
              title="Top Users by Quotation"
              icon={<People sx={{ fontSize: 18 }} />}
              accent={INK}
              actions={
                <>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    {latestUserData.length}
                  </span>
                  <IconButton size="small" onClick={() => downloadFile("project")}>
                    <Download sx={{ fontSize: 18 }} className="text-emerald-600" />
                  </IconButton>
                </>
              }
            >
              <DataTable
                columns={[{ label: "#" }, { label: "Username" }, { label: "Projects", align: "right" }]}
                rows={latestUserData}
                empty="No active users found"
                renderRow={(u, index) => (
                  <tr key={index} className={rowCls}>
                    <td className={`${cellCls} text-slate-400 font-medium`}>{index + 1}</td>
                    <td className={`${cellCls} font-semibold text-slate-800`}>{u.username}</td>
                    <td
                      className={`${cellCls} text-right tabular-nums font-semibold text-slate-700`}
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {fmt(u.project_count)}
                    </td>
                  </tr>
                )}
              />
            </Panel>
          </section>

          {/* Tables Row 2 */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Panel
              title="G3 Price Segment Products"
              icon={<TrendingUp sx={{ fontSize: 18 }} />}
              accent={GOLD}
              actions={
                <>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                    {g3Peoduct.length}
                  </span>
                  <IconButton size="small" onClick={() => downloadFile("g3")}>
                    <Download sx={{ fontSize: 18 }} className="text-emerald-600" />
                  </IconButton>
                </>
              }
            >
              <DataTable
                columns={[{ label: "#" }, { label: "SKU Code" }, { label: "Total Usage", align: "right" }]}
                rows={g3Peoduct}
                empty="No segment data available"
                renderRow={(item, index) => (
                  <tr key={item.project_details_id || index} className={rowCls}>
                    <td className={`${cellCls} text-slate-400 font-medium`}>{index + 1}</td>
                    <td className={`${cellCls} font-semibold text-slate-800`}>{item.sku_code}</td>
                    <td
                      className={`${cellCls} text-right tabular-nums text-slate-700`}
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {fmt(item.total_usage)}
                    </td>
                  </tr>
                )}
              />
            </Panel>

            <Panel
              title="Top 20 Product Segments"
              icon={<ContactMail sx={{ fontSize: 18 }} />}
              accent={CORAL}
              actions={
                <>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">
                    {segmentSkuData.length}
                  </span>
                  <IconButton size="small" onClick={() => downloadFile("priceSegment")}>
                    <Download sx={{ fontSize: 18 }} className="text-emerald-600" />
                  </IconButton>
                </>
              }
            >
              <DataTable
                columns={[{ label: "#" }, { label: "Segment Name" }, { label: "Total Count", align: "right" }]}
                rows={segmentSkuData}
                empty="No segments found"
                renderRow={(segment, index) => (
                  <tr key={index} className={rowCls}>
                    <td className={`${cellCls} text-slate-400 font-medium`}>{index + 1}</td>
                    <td className={`${cellCls} font-semibold text-slate-800`}>{segment.priceSegment}</td>
                    <td
                      className={`${cellCls} text-right tabular-nums text-slate-700`}
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {fmt(segment.count)}
                    </td>
                  </tr>
                )}
              />
            </Panel>
          </section>
        </main>
      </div>
    </>
  );
};

export default withDashboardAccess(UserDashboard);